import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, DatePicker, message, Descriptions, Timeline, Alert, Tooltip, InputNumber } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { copyToClipboard } from '../../utils';
import dayjs from 'dayjs';
import '../../styles/third-party.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_TEXT = {
  pending: '待审核',
  processing: '处理中',
  paid: '通过',
  failed: '失败',
  rejected: '已拒绝'
};

const formatMoney = (val, currencyCode = 'CNY') => {
  const code = currencyCode || 'CNY';
  const num = Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return { num, code };
};

const renderAmount = (val, currencyCode) => {
  const { num, code } = formatMoney(val, currencyCode);
  return (
    <div className="tp-amount">
      <span className="tp-amount__value">{num}</span>
      <span className="tp-amount__ccy">{code}</span>
    </div>
  );
};

const renderKv = (info, max = 3) => {
  if (!info) return <span className="tp-empty">-</span>;
  let data = info;
  if (typeof info === 'string') {
    try { data = JSON.parse(info); } catch (e) { return <span className="tp-ellipsis">{info}</span>; }
  }
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <span className="tp-empty">-</span>;
  return (
    <div className="tp-kv">
      {entries.slice(0, max).map(([k, v]) => (
        <div key={k} className="tp-kv__row">
          <span className="tp-kv__k">{k}</span>
          <span className="tp-kv__v">{String(v)}</span>
        </div>
      ))}
      {entries.length > max && <div className="tp-kv__more">+{entries.length - max}</div>}
    </div>
  );
};

const ThirdPartyWithdrawals = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_amount: 0, page: 1, limit: 20 });
  const [filters, setFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('detail');
  const [detailData, setDetailData] = useState(null);
  const [searchForm] = Form.useForm();
  const [auditForm] = Form.useForm();
  const [appList, setAppList] = useState([]);
  const [changeVisible, setChangeVisible] = useState(false);
  const [changeRecord, setChangeRecord] = useState(null);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeForm] = Form.useForm();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const changeTargetStatus = Form.useWatch('target_status', changeForm);

  useEffect(() => {
    loadData();
    loadAppList();
  }, [pagination.page]);

  const loadAppList = async () => {
    try {
      const response = await api.get('/admin/third-party/apps', { params: { status: 1, limit: 100 } });
      if (response.code === 200) {
        setAppList(response.data.list || []);
      }
    } catch (error) {
      console.error('加载应用列表失败', error);
    }
  };

  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/third-party-withdrawals', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
          ...params
        }
      });

      if (response.code === 200) {
        setDataSource(response.data.list || []);
        setPagination({
          ...pagination,
          total: response.data.pagination.total,
          total_amount: response.data.pagination.total_amount,
          page: response.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
    loadData({ ...newFilters, page: 1 });
  };

  const handleReset = () => {
    searchForm.resetFields();
    setFilters({});
    setPagination({ ...pagination, page: 1 });
    loadData({
      page: 1,
      app_id: undefined,
      status: undefined,
      audit_status: undefined,
      keyword: undefined,
      startDate: undefined,
      endDate: undefined
    });
  };

  const handleViewDetail = async (record) => {
    try {
      setHistoryList([]);
      setHistoryLoading(true);
      const [detailResponse, historyResponse] = await Promise.all([
        api.get(`/admin/third-party-withdrawals/${record.id}`),
        api.get(`/admin/third-party-withdrawals/${record.withdraw_no}/history`)
      ]);
      if (detailResponse.code === 200) {
        setDetailData(detailResponse.data);
      }
      setHistoryList(historyResponse.data || []);
      setModalMode('detail');
      setModalVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAudit = async (record) => {
    try {
      const response = await api.get(`/admin/third-party-withdrawals/${record.id}`);
      if (response.code === 200) {
        setDetailData(response.data);
        const amount = parseFloat(response.data.amount || 0);
        const exchangeRate = parseFloat(response.data.exchange_rate) || 1;
        const feeRate = parseFloat(response.data.fee_rate) || 0;
        auditForm.setFieldsValue({
          audit_status: 'approved',
          audit_remark: '',
          amount: amount,
          cny_amount: amount / exchangeRate,
          fee_amount: amount * feeRate,
          exchange_rate: exchangeRate,
          fee_rate: feeRate
        });
        setModalMode('audit');
        setModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleAuditSubmit = async () => {
    try {
      const values = await auditForm.validateFields();
      const payload = {
        audit_status: values.audit_status,
        audit_remark: values.audit_remark,
        amount: values.amount
      };
      const response = await api.put(`/admin/third-party-withdrawals/${detailData.id}/audit`, payload);
      if (response.code === 200) {
        message.success(response.message || '审核成功');
        setModalVisible(false);
        loadData();
      }
    } catch (error) {
      message.error(error.message || '审核失败');
    }
  };

  const handleChange = async (record) => {
    try {
      const response = await api.get(`/admin/third-party-withdrawals/${record.id}`);
      if (response.code === 200) {
        setChangeRecord(response.data);
        setChangeVisible(true);
        const currentStatus = response.data.status;
        let defaultTarget = 'failed';
        if (currentStatus === 'failed' || currentStatus === 'rejected') {
          defaultTarget = 'paid';
        }
        const amount = parseFloat(response.data.amount || 0);
        const exchangeRate = parseFloat(response.data.exchange_rate) || 1;
        const feeRate = parseFloat(response.data.fee_rate) || 0;
        changeForm.setFieldsValue({
          target_status: defaultTarget,
          amount: amount,
          cny_amount: amount / exchangeRate,
          fee_amount: amount * feeRate,
          exchange_rate: exchangeRate,
          fee_rate: feeRate
        });
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleChangeSubmit = async () => {
    try {
      const values = await changeForm.validateFields();
      setChangeLoading(true);
      const payload = {
        target_status: values.target_status,
        reason: values.reason,
        amount: values.amount
      };
      const response = await api.post(`/admin/third-party-withdrawals/${changeRecord.withdraw_no}/status`, payload);
      if (response.code === 200) {
        message.success('代付状态已变更');
        setChangeVisible(false);
        setChangeRecord(null);
        changeForm.resetFields();
        loadData();
      }
    } catch (error) {
      message.error(error.message || '变更失败');
    } finally {
      setChangeLoading(false);
    }
  };

  const getStatusTag = (status, auditStatus) => {
    if (auditStatus === 'approved') {
      return <span className="tp-tag tp-tag--paid">已通过</span>;
    }
    if (auditStatus === 'rejected') {
      return <span className="tp-tag tp-tag--rejected">已拒绝</span>;
    }
    const toneMap = {
      pending: 'processing',
      processing: 'pending',
      paid: 'paid',
      failed: 'failed'
    };
    return <span className={`tp-tag tp-tag--${toneMap[status] || 'muted'}`}>{STATUS_TEXT[status] || status}</span>;
  };

  const isBankAccountKey = (key) => String(key).replace(/[_\s-]/g, '').toLowerCase() === 'bankaccount';

  const handleCopyAccount = async (value) => {
    const text = value == null ? '' : String(value);
    if (!text) {
      message.warning('账户为空');
      return;
    }
    const ok = await copyToClipboard(text);
    if (ok) {
      message.success('账户已复制');
    } else {
      message.error('复制失败');
    }
  };

  const renderWithdrawInfo = () => {
    if (!detailData?.withdraw_info) return '-';
    
    const info = typeof detailData.withdraw_info === 'string' 
      ? JSON.parse(detailData.withdraw_info) 
      : detailData.withdraw_info;

    if (!info || Object.keys(info).length === 0) return '-';

    return (
      <div className="tp-info-box">
        {Object.entries(info).map(([key, value]) => (
          <div className="tp-info-box__row" key={key}>
            <span className="tp-info-box__k">{key}</span>
            <span className="tp-info-box__v">
              {value}
              {isBankAccountKey(key) && value != null && value !== '' && (
                <Button
                  type="link"
                  size="small"
                  className="tp-copy"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyAccount(value)}
                >
                  复制
                </Button>
              )}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: '代付单号',
      dataIndex: 'withdraw_no',
      key: 'withdraw_no',
      width: 188,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '验证短码',
      dataIndex: 'short_code',
      key: 'short_code',
      width: 110,
      render: (code) => code ? <span className="tp-code">{code}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 110,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '代付金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      render: (val, record) => renderAmount(val, record.currency_code)
    },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
      width: 100,
      render: (val) => val != null && val !== '' ? <span className="tp-mono">{Number(val).toFixed(2)}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '代付方式',
      dataIndex: 'withdraw_method',
      key: 'withdraw_method',
      width: 110,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '代付信息',
      dataIndex: 'withdraw_info',
      key: 'withdraw_info',
      width: 220,
      render: (info) => renderKv(info, 3)
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status, record) => (
        <div className="tp-status">
          {getStatusTag(status, record.audit_status)}
          {record.is_modified == 1 && (
            <span className="tp-tag tp-tag--modified">已修改 ×{record.modify_count || 1}</span>
          )}
        </div>
      )
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val) => val ? <span className="tp-time">{dayjs(val).format('YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 176,
      fixed: 'right',
      render: (_, record) => (
        <div className="tp-actions">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {record.audit_status === 'pending' && (
            <Button type="link" size="small" onClick={() => handleAudit(record)}>
              审核
            </Button>
          )}
          {(record.status === 'paid' || record.status === 'failed' || record.status === 'rejected') && record.audit_status !== 'pending' && (
            <Tooltip title={`变更代付状态（当前：${STATUS_TEXT[record.status] || record.status}）`}>
              <Button type="link" size="small" className="tp-link--change" icon={<EditOutlined />} onClick={() => handleChange(record)}>
                变更
              </Button>
            </Tooltip>
          )}
        </div>
      )
    }
  ];

  const renderModalFooter = () => {
    if (modalMode === 'detail') {
      return [
        <Button key="close" onClick={() => setModalVisible(false)}>关闭</Button>
      ];
    }
    return [
      <Button key="cancel" onClick={() => setModalVisible(false)}>取消</Button>,
      <Button key="submit" type="primary" onClick={handleAuditSubmit}>提交审核</Button>
    ];
  };

  return (
    <div className="tp-page">
      <div className="tp-card tp-toolbar">
        <Form form={searchForm} layout="inline" className="tp-filters" onFinish={handleSearch}>
          <Form.Item name="app_id">
            <Select placeholder="选择应用" className="tp-field--md" allowClear showSearch optionFilterProp="children">
              {appList.map(app => (
                <Option key={app.app_id} value={app.app_id}>{app.app_name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="订单状态" className="tp-field--sm" allowClear>
              <Option value="pending">待审核</Option>
              <Option value="processing">处理中</Option>
              <Option value="paid">通过</Option>
              <Option value="failed">失败</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="audit_status">
            <Select placeholder="审核状态" className="tp-field--sm" allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="keyword">
            <Input placeholder="代付单号" className="tp-field--md" allowClear />
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker className="tp-field--date" />
          </Form.Item>
          <div className="tp-filters__actions">
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          </div>
        </Form>
      </div>

      <div className="tp-card tp-summary">
        <div className="tp-summary__item">
          <div className="tp-summary__label">代付总笔数</div>
          <div className="tp-summary__value">{Number(pagination.total || 0).toLocaleString('en-US')}</div>
        </div>
        <div className="tp-summary__item">
          <div className="tp-summary__label">代付总金额</div>
          <div className="tp-summary__value tp-summary__value--green">
            {Number(pagination.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      <div className="tp-card tp-table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          size="middle"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, limit: pageSize });
            }
          }}
          scroll={{ x: 1420 }}
        />
      </div>

      <Modal
        className="tp-modal"
        title={modalMode === 'audit' ? '审核代付' : '代付详情'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={renderModalFooter()}
        width={800}
      >
        {detailData && (
          <div>
            <div className="tp-section">
              <div className="tp-section__title">基本信息</div>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="代付单号"><span className="tp-mono">{detailData.withdraw_no}</span></Descriptions.Item>
                <Descriptions.Item label="验证短码">
                  {detailData.short_code ? <span className="tp-code">{detailData.short_code}</span> : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="应用名称">{detailData.app_name}</Descriptions.Item>
                <Descriptions.Item label="代付方式">{detailData.withdraw_method}</Descriptions.Item>
                <Descriptions.Item label="代付金额">
                  {detailData.currency_code} {Number(detailData.amount || 0).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="汇率">{detailData.exchange_rate || 1}</Descriptions.Item>
                <Descriptions.Item label="手续费">{Number(detailData.fee_amount || 0).toFixed(2)}</Descriptions.Item>
                <Descriptions.Item label="实际到账">{Number(detailData.actual_amount || 0).toFixed(2)}</Descriptions.Item>
                <Descriptions.Item label="回调地址">{detailData.callback_url || '-'}</Descriptions.Item>
                <Descriptions.Item label="状态">{getStatusTag(detailData.status, detailData.audit_status)}</Descriptions.Item>
                <Descriptions.Item label="审核状态">
                  {detailData.audit_status === 'approved' ? '已通过' : detailData.audit_status === 'rejected' ? '已拒绝' : '待审核'}
                </Descriptions.Item>
                <Descriptions.Item label="交易号">{detailData.transaction_id || '-'}</Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {detailData.created_at ? dayjs(detailData.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="审核时间">
                  {detailData.audit_time ? dayjs(detailData.audit_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="审核人">{detailData.audit_by_name || '-'}</Descriptions.Item>
              </Descriptions>
            </div>

            <div className="tp-section">
              <div className="tp-section__title">代付信息</div>
              {renderWithdrawInfo()}
            </div>

            {detailData.audit_status !== 'pending' && (
              <div className="tp-section">
                <div className="tp-section__title">审核信息</div>
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="审核备注">{detailData.audit_remark || '-'}</Descriptions.Item>
                </Descriptions>
              </div>
            )}

            <div className="tp-history">
              <div className="tp-history__title">审核 / 变更记录</div>
              {historyLoading ? (
                <div className="tp-history__empty">加载中...</div>
              ) : historyList.length === 0 ? (
                <div className="tp-history__empty">暂无记录</div>
              ) : (
                <Timeline
                  items={historyList.map((item) => ({
                    color: item.is_reversal ? 'red' : (item.new_status === 'paid' ? 'green' : 'orange'),
                    children: (
                      <div>
                        <Space size={4} wrap>
                          <span className="tp-history__meta">{dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}</span>
                          <span className={`tp-tag ${item.is_modified ? 'tp-tag--modified' : 'tp-tag--pending'}`}>{item.is_modified ? '变更' : '确认'}</span>
                          {item.is_reversal ? <span className="tp-tag tp-tag--failed">冲正</span> : null}
                        </Space>
                        <div className="tp-history__flow">
                          {STATUS_TEXT[item.old_status] || item.old_status || '-'}
                          {' → '}
                          {STATUS_TEXT[item.new_status] || item.new_status}
                        </div>
                        {item.amount != null && (
                          <div className="tp-history__line">
                            {item.original_amount != null ? (
                              <>金额: <span className="tp-history__muted tp-strike">{item.original_amount}</span> → <strong>{item.amount}</strong> {detailData?.currency_code || 'CNY'}</>
                            ) : (
                              <>金额: {item.amount} {detailData?.currency_code || 'CNY'}</>
                            )}
                          </div>
                        )}
                        {item.operator_name && (
                          <div className="tp-history__line tp-history__muted">操作人: {item.operator_name}</div>
                        )}
                        {item.remark && (
                          <div className="tp-history__line">备注: {item.remark}</div>
                        )}
                      </div>
                    ),
                  }))}
                />
              )}
            </div>

            {modalMode === 'audit' && detailData.audit_status === 'pending' && (
              <div className="tp-section">
                <div className="tp-section__title">审核操作</div>
                <Form form={auditForm} layout="vertical"
                  onValuesChange={(changedValues, allValues) => {
                    if (changedValues.amount !== undefined) {
                      const amount = parseFloat(changedValues.amount) || 0;
                      const exchangeRate = parseFloat(allValues.exchange_rate) || 1;
                      const feeRate = parseFloat(allValues.fee_rate) || 0;
                      auditForm.setFieldsValue({
                        cny_amount: amount / exchangeRate,
                        fee_amount: amount * feeRate
                      });
                    }
                  }}
                >
                  <Form.Item label="代付金额">
                    <Input value={`${detailData.currency_code || 'CNY'} ${Number(detailData.amount || 0).toFixed(2)}`} disabled />
                  </Form.Item>
                  <Form.Item
                    label="新代付金额"
                    name="amount"
                    rules={[
                      { required: true, message: '请输入代付金额' },
                      { type: 'number', min: 0.01, message: '金额必须大于0' }
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0.01}
                      step={0.01}
                      precision={2}
                      placeholder="请输入代付金额（不修改则保持原金额）"
                      addonAfter={detailData.currency_code || 'CNY'}
                    />
                  </Form.Item>
                  <Form.Item label="手续费" name="fee_amount">
                    <InputNumber
                      style={{ width: '100%' }}
                      disabled
                      addonAfter={detailData.currency_code || 'CNY'}
                    />
                  </Form.Item>
                  <Form.Item name="audit_status" label="审核结果" rules={[{ required: true, message: '请选择审核结果' }]}>
                    <Select placeholder="请选择审核结果">
                      <Option value="approved">通过</Option>
                      <Option value="rejected">拒绝</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="audit_remark" label="审核备注">
                    <TextArea rows={3} placeholder="请输入审核备注（可选）" />
                  </Form.Item>
                </Form>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        className="tp-modal"
        title="变更代付状态"
        open={changeVisible}
        onCancel={() => {
          setChangeVisible(false);
          setChangeRecord(null);
          changeForm.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {changeRecord && (
          <Form
            form={changeForm}
            layout="vertical"
            onFinish={handleChangeSubmit}
            onValuesChange={(changedValues, allValues) => {
              if (changedValues.amount !== undefined) {
                const amount = parseFloat(changedValues.amount) || 0;
                const exchangeRate = parseFloat(allValues.exchange_rate) || 1;
                const feeRate = parseFloat(allValues.fee_rate) || 0;
                changeForm.setFieldsValue({
                  cny_amount: amount / exchangeRate,
                  fee_amount: amount * feeRate
                });
              }
            }}
          >
            {changeTargetStatus === 'failed' && (
              <Form.Item>
                <Alert
                  type="warning"
                  showIcon
                  message="该操作将恢复商户余额"
                  description="代付状态变更为失败后，将恢复商户被扣减的余额。"
                />
              </Form.Item>
            )}
            {changeTargetStatus === 'paid' && (
              <Form.Item>
                <Alert
                  type="info"
                  showIcon
                  message="重新确认为通过"
                  description="确认后代付状态将变更为通过，并扣减商户余额。"
                />
              </Form.Item>
            )}
            <Form.Item label="代付单号">
              <Input value={changeRecord.withdraw_no} disabled />
            </Form.Item>
            <Form.Item label="应用名称">
              <Input value={changeRecord.app_name} disabled />
            </Form.Item>
            <Form.Item label="当前状态">
              <Input value={STATUS_TEXT[changeRecord.status] || changeRecord.status} disabled />
            </Form.Item>
            <Form.Item label="代付金额">
              <Input value={`${changeRecord.currency_code || 'CNY'} ${Number(changeRecord.amount || 0).toFixed(2)}`} disabled />
            </Form.Item>
            <Form.Item
              label="目标状态"
              name="target_status"
              rules={[{ required: true, message: '请选择目标状态' }]}
            >
              <Select placeholder="请选择目标状态">
                <Option value="paid">通过</Option>
                <Option value="failed">失败</Option>
              </Select>
            </Form.Item>
            {changeTargetStatus === 'paid' && (
              <>
                <Form.Item
                  label="代付金额"
                  name="amount"
                  rules={[
                    { required: true, message: '请输入代付金额' },
                    { type: 'number', min: 0.01, message: '金额必须大于0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0.01}
                    step={0.01}
                    precision={2}
                    placeholder="请输入代付金额"
                    addonAfter={changeRecord.currency_code || 'CNY'}
                  />
                </Form.Item>
                <Form.Item label="手续费" name="fee_amount">
                  <InputNumber
                    style={{ width: '100%' }}
                    disabled
                    addonAfter={changeRecord.currency_code || 'CNY'}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item
              label="变更备注"
              name="reason"
              rules={[{ required: true, message: '请输入变更备注' }]}
            >
              <TextArea rows={3} placeholder="请输入变更备注" />
            </Form.Item>
            <Form.Item className="tp-modal-actions">
              <Space>
                <Button onClick={() => setChangeVisible(false)}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit" loading={changeLoading}>
                  确认变更
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );

};

export default ThirdPartyWithdrawals;

